"use client";

// Families & groups management: a house belongs to a named family with n
// members. Moderators/admins create families, place validated users into
// them, rename/remove them, and see which properties each family owns.

import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost, apiPatch, apiDelete } from "@/lib/api-client";
import type { FamilyGroup, FamilyMember } from "@/lib/api-client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { roleBadgeClass, roleLabel } from "@/components/booking/format";
import {
  Users,
  Plus,
  UserMinus,
  Pencil,
  Trash2,
  Home,
  UserPlus,
} from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n/provider";

type Candidates = FamilyMember[];

export function GroupsPanel() {
  const qc = useQueryClient();
  const { t, locale } = useI18n();
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [renameFor, setRenameFor] = useState<FamilyGroup | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [addMemberFor, setAddMemberFor] = useState<FamilyGroup | null>(null);
  const [addMemberChoice, setAddMemberChoice] = useState<string>("");

  const { data, isLoading } = useQuery({
    queryKey: ["families"],
    queryFn: () =>
      apiGet<{
        families: FamilyGroup[];
        candidates: Candidates;
      }>("/api/admin/families"),
  });

  const families = data?.families ?? [];
  const candidates = data?.candidates ?? [];
  const guests = candidates.filter((c) => c.userGroup === "GUEST");
  const unassignedFamily = candidates.filter((c) => c.userGroup === "FAMILY");

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["families"] });
    qc.invalidateQueries({ queryKey: ["admin-users"] });
    qc.invalidateQueries({ queryKey: ["dashboard"] });
  };

  const createFamily = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await apiPost("/api/admin/families", { name: newName });
      toast.success(t("groups.familyCreated"));
      setNewName("");
      setCreateOpen(false);
      refresh();
    } catch (err: any) {
      toast.error(err.message || "Could not create family");
    }
  };

  const renameFamily = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!renameFor) return;
    try {
      await apiPatch(`/api/admin/families/${renameFor.id}`, { name: renameValue });
      toast.success(t("groups.familyRenamed"));
      setRenameFor(null);
      refresh();
    } catch (err: any) {
      toast.error(err.message || "Could not rename");
    }
  };

  const deleteFamily = async (f: FamilyGroup) => {
    if (!window.confirm(t("groups.deleteConfirm", { name: f.name }))) return;
    try {
      await apiDelete(`/api/admin/families/${f.id}`);
      toast.success(t("groups.familyDeleted"));
      refresh();
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const addMember = async (familyId: string, userId: string) => {
    try {
      const res = await apiPost<{ member: FamilyMember }>(
        `/api/admin/families/${familyId}/members`,
        { userId }
      );
      const fam = families.find((f) => f.id === familyId);
      toast.success(
        t("groups.memberAdded", { name: res.member.name, family: fam?.name ?? "" })
      );
      setAddMemberFor(null);
      setAddMemberChoice("");
      refresh();
    } catch (err: any) {
      toast.error(err.message || "Could not add member");
    }
  };

  const removeMember = async (familyId: string, m: FamilyMember) => {
    if (!window.confirm(t("groups.removeConfirm", { name: m.name }))) return;
    try {
      await apiDelete(`/api/admin/families/${familyId}/members/${m.id}`);
      toast.success(t("groups.memberRemoved", { name: m.name }));
      refresh();
    } catch (err: any) {
      toast.error(err.message || "Could not remove member");
    }
  };

  const memberRow = (familyId: string) => (m: FamilyMember) => (
    <div
      key={m.id}
      className="flex items-center justify-between gap-2 rounded-lg border px-3 py-2"
    >
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="truncate text-sm font-medium">{m.name}</span>
          <span className="text-xs text-muted-foreground">@{m.username}</span>
          <Badge variant="outline" className={`h-5 ${roleBadgeClass(m.role)}`}>
            {roleLabel(m.role, locale)}
          </Badge>
          {!m.validated && (
            <Badge
              variant="outline"
              className="h-5 border-amber-300 bg-amber-50 text-amber-700"
            >
              {t("mod.notValidated")}
            </Badge>
          )}
        </div>
      </div>
      <Button
        size="icon"
        variant="ghost"
        title={t("groups.removeMember")}
        className="shrink-0 text-muted-foreground hover:text-destructive"
        onClick={() => removeMember(familyId, m)}
      >
        <UserMinus className="h-4 w-4" />
      </Button>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="max-w-xl text-sm text-muted-foreground">{t("groups.subtitle")}</p>
        <Button size="sm" className="gap-1.5" onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4" /> {t("groups.create")}
        </Button>
      </div>

      {isLoading ? (
        <Skeleton className="h-48 w-full rounded-xl" />
      ) : families.length === 0 ? (
        <div className="grid place-items-center rounded-xl border border-dashed py-12 text-center text-sm text-muted-foreground">
          {t("groups.noFamilies")}
        </div>
      ) : (
        <div className="grid gap-4 xl:grid-cols-2">
          {families.map((f) => (
            <Card key={f.id}>
              <CardContent className="space-y-3 p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-emerald-600" />
                    <span className="font-semibold">{f.name}</span>
                    <Badge variant="outline" className="h-5">
                      {t("groups.members", { count: f.members.length })}
                    </Badge>
                    {f.products.length > 0 && (
                      <Badge variant="outline" className="h-5">
                        <Home className="me-1 h-3 w-3" />
                        {t("groups.properties", { count: f.products.length })}
                      </Badge>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <Button
                      size="icon"
                      variant="ghost"
                      title={t("groups.rename")}
                      onClick={() => {
                        setRenameFor(f);
                        setRenameValue(f.name);
                      }}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      title={t("groups.delete")}
                      className="text-muted-foreground hover:text-destructive"
                      onClick={() => deleteFamily(f)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {f.products.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {f.products.map((p) => (
                      <Badge
                        key={p.id}
                        variant="outline"
                        className="border-sky-200 bg-sky-50 text-sky-700"
                      >
                        <Home className="me-1 h-3 w-3" />
                        {p.title}
                      </Badge>
                    ))}
                  </div>
                )}

                <div className="space-y-2">
                  {f.members.length === 0 ? (
                    <p className="rounded-lg border border-dashed p-3 text-center text-xs text-muted-foreground">
                      {t("groups.candidatesEmpty")}
                    </p>
                  ) : (
                    f.members.map(memberRow(f.id))
                  )}
                </div>

                <Button
                  size="sm"
                  variant="outline"
                  className="w-full gap-1.5"
                  onClick={() => {
                    setAddMemberFor(f);
                    setAddMemberChoice("");
                  }}
                >
                  <UserPlus className="h-4 w-4" /> {t("groups.addMember")}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Guests outside any family */}
      <Card>
        <CardContent className="p-4">
          <div className="mb-1 font-medium">{t("groups.guests")}</div>
          <p className="mb-3 text-xs text-muted-foreground">{t("groups.guestsHint")}</p>
          {guests.length === 0 ? (
            <p className="rounded-lg border border-dashed p-3 text-center text-xs text-muted-foreground">
              {t("groups.candidatesEmpty")}
            </p>
          ) : (
            <div className="space-y-2">
              {guests.map((g) => (
                <div
                  key={g.id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-lg border px-3 py-2"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium">{g.name}</span>
                    <span className="text-xs text-muted-foreground">@{g.username}</span>
                    {!g.validated && (
                      <Badge
                        variant="outline"
                        className="h-5 border-amber-300 bg-amber-50 text-amber-700"
                      >
                        {t("mod.notValidated")}
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Unassigned FAMILY members (edge case) */}
      {unassignedFamily.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <div className="mb-3 font-medium">{t("groups.unassignedFam")}</div>
            <div className="space-y-2">
              {unassignedFamily.map((m) => (
                <div
                  key={m.id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-lg border px-3 py-2"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium">{m.name}</span>
                    <span className="text-xs text-muted-foreground">@{m.username}</span>
                  </div>
                  <Select onValueChange={(v) => addMember(v, m.id)}>
                    <SelectTrigger className="w-[220px]">
                      <SelectValue placeholder={t("groups.addMember")} />
                    </SelectTrigger>
                    <SelectContent>
                      {families.map((f) => (
                        <SelectItem key={f.id} value={f.id}>
                          {f.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create family */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("groups.create")}</DialogTitle>
            <DialogDescription>{t("groups.subtitle")}</DialogDescription>
          </DialogHeader>
          <form onSubmit={createFamily} className="grid gap-4">
            <div className="grid gap-1.5">
              <Label>{t("groups.name")}</Label>
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder={t("groups.namePlaceholder")}
                required
                minLength={2}
                maxLength={80}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>
                {t("common.cancel")}
              </Button>
              <Button type="submit">{t("groups.create")}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Rename family */}
      <Dialog open={!!renameFor} onOpenChange={(o) => !o && setRenameFor(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("groups.rename")}</DialogTitle>
            <DialogDescription>{renameFor?.name}</DialogDescription>
          </DialogHeader>
          <form onSubmit={renameFamily} className="grid gap-4">
            <div className="grid gap-1.5">
              <Label>{t("groups.name")}</Label>
              <Input
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                required
                minLength={2}
                maxLength={80}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setRenameFor(null)}>
                {t("common.cancel")}
              </Button>
              <Button type="submit">{t("groups.rename")}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add member */}
      <Dialog open={!!addMemberFor} onOpenChange={(o) => !o && setAddMemberFor(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-emerald-600" /> {t("groups.addMember")}
            </DialogTitle>
            <DialogDescription>{addMemberFor?.name}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4">
            <div className="grid gap-1.5">
              <Label>{t("groups.addMember")}</Label>
              <Select value={addMemberChoice} onValueChange={setAddMemberChoice}>
                <SelectTrigger>
                  <SelectValue placeholder={t("groups.candidatesEmpty")} />
                </SelectTrigger>
                <SelectContent>
                  {candidates.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name} (@{c.username}) · {roleLabel(c.role, locale)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddMemberFor(null)}>
                {t("common.cancel")}
              </Button>
              <Button
                disabled={!addMemberChoice}
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={() => addMemberFor && addMember(addMemberFor.id, addMemberChoice)}
              >
                {t("groups.addMember")}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
